"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Terminal } from "lucide-react";

import { cn } from "@/lib/utils";
import { DOCS_NAV, type DocNavSection } from "./nav";
import { PRIMARY_GRADIENT, SHADOW_ICON } from "./theme";

function SidebarSection({
  section,
  pathname,
  onNavigate,
}: {
  section: DocNavSection;
  pathname: string | null;
  onNavigate?: () => void;
}) {
  if (section.highlight) {
    const item = section.items[0];
    const active = pathname === item.href;
    return (
      <Link
        href={item.href}
        onClick={onNavigate}
        className={cn(
          "flex items-center gap-3 rounded-xl border p-3 transition-colors",
          active
            ? "border-lime-400/40 bg-lime-400/10"
            : "border-border/60 bg-gradient-to-r from-zinc-100/70 via-background to-zinc-100/50 hover:border-zinc-400/60 dark:from-zinc-900/80 dark:via-background dark:to-zinc-900/60 dark:hover:border-zinc-600",
        )}
      >
        <div className={cn("flex size-8 shrink-0 items-center justify-center", SHADOW_ICON)}>
          <Terminal className="size-4" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            {item.title}
            <span className={cn("rounded-full px-1.5 py-px text-[10px] font-medium", PRIMARY_GRADIENT)}>
              New
            </span>
          </div>
          <div className="text-xs text-muted-foreground">Ask your agent for the data</div>
        </div>
      </Link>
    );
  }

  return (
    <div>
      <div className="mb-1.5 px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {section.crumb ? `${section.crumb} · ${section.title}` : section.title}
      </div>
      <div className="grid gap-px">
        {section.items.map((item) => {
          const active = pathname === item.href;
          return (
            <Link
              key={item.href}
              href={item.href}
              onClick={onNavigate}
              className={cn(
                "flex items-center justify-between rounded-lg px-2 py-1.5 text-sm transition-colors",
                active
                  ? cn("font-medium", PRIMARY_GRADIENT)
                  : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
              )}
            >
              {item.title}
              {item.badge ? (
                <span
                  className={cn(
                    "rounded-full border px-1.5 py-px text-[10px]",
                    active
                      ? "border-zinc-900/20 text-zinc-900"
                      : "border-border/60 text-muted-foreground",
                  )}
                >
                  {item.badge}
                </span>
              ) : null}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export function DocsSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const pathname = usePathname();
  return (
    <nav className="flex flex-col gap-6">
      {DOCS_NAV.map((section) => (
        <SidebarSection
          key={section.title}
          section={section}
          pathname={pathname}
          onNavigate={onNavigate}
        />
      ))}
    </nav>
  );
}
